import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentIcon, ShoppingBagIcon, CurrencyDollarIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { SupplierOffer, OfferDocument, supplierOffersAPI, API_BASE_URL } from '../../services/api';

interface SupplierOfferDetailProps {
  isOpen: boolean;
  onClose: () => void;
  offer: SupplierOffer | null;
}

const SupplierOfferDetail: React.FC<SupplierOfferDetailProps> = ({ isOpen, onClose, offer }) => {
  const [documents, setDocuments] = useState<OfferDocument[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && offer) {
      fetchDocuments();
    } else {
      setDocuments([]);
    }
  }, [isOpen, offer]);

  const fetchDocuments = async () => {
    if (!offer) return;
    try {
      setLoading(true);
      const docs = await supplierOffersAPI.getDocuments(offer.id);
      setDocuments(docs);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case 'SPECIFICATION':
        return 'bg-primary-100 text-primary-800';
      case 'CERTIFICATE':
        return 'bg-success-100 text-success-800';
      case 'DATASHEET':
        return 'bg-secondary-100 text-secondary-800';
      case 'INVOICE':
        return 'bg-warning-100 text-warning-800';
      case 'CONTRACT':
        return 'bg-error-100 text-error-800';
      case 'IMAGE':
        return 'bg-secondary-100 text-secondary-800';
      case 'COA':
        return 'bg-success-100 text-success-800';
      case 'MSDS':
        return 'bg-warning-100 text-warning-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-warning-100 text-warning-800';
      case 'ACCEPTED':
        return 'bg-success-100 text-success-800';
      case 'REJECTED':
        return 'bg-error-100 text-error-800';
      case 'EXPIRED':
        return 'bg-neutral-100 text-neutral-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (!isOpen || !offer) return null;

  return (
    <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-gradient-to-r from-primary-600 to-primary-700">
          <div className="flex items-center">
            <ShoppingBagIcon className="h-8 w-8 text-white mr-3" />
            <div>
              <h3 className="text-xl font-semibold text-white">{offer.productName}</h3>
              <p className="text-sm text-primary-100">Supplier Offer #{offer.id}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-primary-100 transition-colors">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Supplier Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
              Supplier Information
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Supplier Name</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.supplier?.name || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Status</label>
                <span className={`inline-flex mt-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(offer.status)}`}>
                  {offer.status}
                </span>
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <ShoppingBagIcon className="h-5 w-5 mr-2 text-primary-600" />
              Product Information
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-neutral-500">Product Name</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.productName}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-neutral-500">Description</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.description || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Quantity</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.quantity}</p>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
              Pricing
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Unit Price</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.currency} {offer.unitPrice}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Total Price</label>
                <p className="mt-1 text-lg font-semibold text-neutral-900">{offer.currency} {offer.totalPrice}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Currency</label>
                <p className="mt-1 text-sm text-neutral-900">{offer.currency}</p>
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
              Validity Period
            </h4>
            <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-neutral-500">Valid From</label>
                <p className="mt-1 text-sm text-neutral-900">
                  {new Date(offer.validFrom).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-500">Valid Until</label>
                <p className="mt-1 text-sm text-neutral-900">
                  {offer.validUntil ? new Date(offer.validUntil).toLocaleDateString() : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {offer.notes && (
            <div>
              <h4 className="text-lg font-semibold text-neutral-900 mb-4">Notes</h4>
              <div className="bg-neutral-50 p-4 rounded-lg">
                <p className="text-sm text-neutral-900 whitespace-pre-wrap">{offer.notes}</p>
              </div>
            </div>
          )}

          {/* Documents */}
          <div>
            <h4 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
              <DocumentIcon className="h-5 w-5 mr-2 text-primary-600" />
              Documents ({documents.length})
            </h4>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : documents.length > 0 ? (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-200 hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <DocumentIcon className="h-5 w-5 text-neutral-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-neutral-900 truncate">{doc.fileName}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFileTypeColor(doc.fileType)}`}>
                            {doc.fileType}
                          </span>
                          <span className="text-xs text-neutral-500">{formatFileSize(doc.fileSize)}</span>
                          <span className="text-xs text-neutral-500">
                            {new Date(doc.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <a
                      href={`${API_BASE_URL}/${doc.fileUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded hover:bg-primary-100 transition-colors"
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-neutral-50 rounded-lg">
                <DocumentIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <p className="mt-2 text-sm text-neutral-500">No documents uploaded</p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-4 text-sm text-neutral-500">
              <div>
                <span className="font-medium">Created:</span> {new Date(offer.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="font-medium">Updated:</span> {new Date(offer.updatedAt).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-neutral-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierOfferDetail;

